import type { DropTargetEdge } from "@v1/types/drop-target";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { successToast } from "@v1/components/designSystem/alert/Toast";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import useSetDraftElement from "@v1/hooks/useSetDraftElement";
import { useStoreAndDraftElementProducts } from "@v1/hooks/useStoreProducts";
import { useV1ErrorToast } from "@v1/hooks/useV1ErrorToast";
import { selectLocaleData } from "@v1/reducers/commerce-reducer";
import {
  selectComponentDataMapping,
  selectComponentMapping,
  selectDraftComponentId,
  selectDraftComponentType,
  selectDraftElement_warningThisWillRerenderOnEveryUpdate,
  selectGetAttribute,
  selectImageSourceComponentProps,
  selectRootComponentId,
} from "@v1/reducers/core-reducer";
import {
  addEditorMediaUploadingComponentId,
  resetEditorMediaUploadingComponentId,
} from "@v1/reducers/editor-media-reducer";
import { selectTemplateEditorStoreProduct } from "@v1/reducers/template-reducer";
import {
  useEditorDispatch,
  useEditorSelector,
  useEditorStore,
} from "@v1/store/store";
import { finalizeDropComponentActions } from "@v1/utils/drop";
import { getImageSourceComponentActions } from "@v1/utils/getImageSourceComponentActions";
import getUpdatedImageComponentTemplate from "@v1/utils/getUpadatedImageComponentTemplate";
import { IMAGE_ACCEPT_FILE_EXTENSIONS } from "@v2/features/assets/constants";
import { useReploFileDropZone } from "@v2/features/assets/useFileDropZone";
import { getFromRecordOrNull } from "replo-runtime/shared/utils/optional";
import { image } from "replo-templates/image";

import {
  selectActiveCanvas,
  selectCanvasDeltaXY,
  selectCanvases,
  setDeltaXY,
} from "./canvas-reducer";

export const ReploCanvasFileDropZone: React.FC<
  React.PropsWithChildren<{
    role?: string;
    className?: string;
    style?: React.CSSProperties;
  }>
> = ({ children, role, className, style }) => {
  const store = useEditorStore();
  const dispatch = useEditorDispatch();
  const applyComponentAction = useApplyComponentAction();
  const setDraftElement = useSetDraftElement();
  const { products } = useStoreAndDraftElementProducts();
  const draftComponentId = useEditorSelector(selectDraftComponentId);
  const {
    activeCurrency,
    activeLanguage,
    moneyFormat,
    moneyWithCurrencyFormat,
  } = useEditorSelector(selectLocaleData);
  const canvases = useEditorSelector(selectCanvases);
  const activeCanvas = useEditorSelector(selectActiveCanvas);
  const activeCanvasWidth = canvases[activeCanvas].canvasWidth;
  const templateProduct =
    useEditorSelector(selectTemplateEditorStoreProduct) ?? null;
  const errorToast = useV1ErrorToast();

  const onDropBeforeUpload = () => {
    const draftComponentType = selectDraftComponentType(store.getState());
    const draftComponentId = selectDraftComponentId(store.getState());
    const draftElement =
      selectDraftElement_warningThisWillRerenderOnEveryUpdate(store.getState());
    const rootComponentId = selectRootComponentId(store.getState());

    if (!draftElement || !rootComponentId) {
      return null;
    }

    if (draftComponentType !== "image") {
      const componentIdToDrag = draftComponentId ?? rootComponentId;

      const target = {
        componentId: componentIdToDrag,
        edge: "top" as DropTargetEdge,
        error: null,
      };

      const imageTemplate = getUpdatedImageComponentTemplate(
        image,
        componentIdToDrag === rootComponentId
          ? 0
          : selectCanvasDeltaXY(store.getState()).deltaY,
      );

      const result = finalizeDropComponentActions(
        target,
        imageTemplate,
        draftElement,
        componentIdToDrag,
        selectGetAttribute(store.getState()),
        selectComponentDataMapping(store.getState()),
        {
          products,
          currencyCode: activeCurrency,
          language: activeLanguage,
          moneyFormat,
          moneyWithCurrencyFormat,
          templateProduct,
          isEditor: true,
          isShopifyProductsLoading: false,
        },
      );

      if (result.result === "error") {
        errorToast(
          "Cannot add image here",
          "Please try dropping the image into a different container or section. If this issue persists, try selecting a container first before dropping the image.",
          "error.file.upload",
          {
            error: result.message ?? "No message for error",
          },
        );
      } else if (result.result === "success") {
        const componentActions = result.actions;
        if (componentActions.length > 0) {
          applyComponentAction({
            type: "applyCompositeAction",
            value: componentActions,
            activeCanvas: componentActions[0]!.activeCanvas,
          });

          if (result.addedComponent) {
            dispatch(
              addEditorMediaUploadingComponentId(result.addedComponent.id),
            );
            setDraftElement({
              componentIds: [result.addedComponent.id],
            });

            // NOTE (Fran 2024-05-03): If the user does not select anything before the image is dropped,
            // we will add the image at the top of the root component. In that case, we need to send
            // they to the top of the canvas.
            if (componentIdToDrag === rootComponentId) {
              dispatch(
                setDeltaXY({
                  deltaX: selectCanvasDeltaXY(store.getState()).deltaX ?? 0,
                  deltaY: 0,
                  frameWidth: activeCanvasWidth,
                }),
              );
            }

            return result.addedComponent.id;
          }
        }
      }
    } else if (draftComponentId) {
      // NOTE (Fran 2024-05-01): We need to remove the old image source when we upload a new image.
      applyComponentAction({
        type: "setStyles",
        value: {
          __imageSource: null,
        },
      });
      dispatch(addEditorMediaUploadingComponentId(draftComponentId));
      return draftComponentId;
    }

    return null;
  };

  const onUploadComplete = ({
    url: publicUrl,
    imageComponentId,
  }: {
    url: string;
    imageComponentId: string | null;
  }) => {
    // NOTE (Fran 2024-05-03): It's possible the user deselects the image component before the upload is complete.
    // In that case, we need to know which component we need to update, so we will use the id of the
    // new image component id created at the moment that the image was dropped.
    if (publicUrl && imageComponentId) {
      const componentDataMapping = selectComponentDataMapping(store.getState());
      // NOTE (Fran 2024-05-07): We need to check if the component still exists in the element.
      if (imageComponentId in componentDataMapping) {
        const component = getFromRecordOrNull(
          selectComponentMapping(store.getState()),
          imageComponentId,
        )?.component;
        const getAttribute = selectGetAttribute(store.getState());
        // NOTE (Fran 2024-05-07): We will only replace the image source if the current image source is empty.
        const imageSource = getAttribute(
          component ?? null,
          "style.__imageSource",
        );
        if (!imageSource.value) {
          const {
            imageComponentId: _imageComponentId,
            imageSourceProp,
            imageUrl,
          } = selectImageSourceComponentProps(
            store.getState(),
            publicUrl,
            imageComponentId,
          );
          const actions = getImageSourceComponentActions({
            canvas: activeCanvas,
            targetComponentId:
              _imageComponentId ?? draftComponentId ?? undefined,
            imageSourceProp,
            imageUrl,
          });

          applyComponentAction({
            type: "applyCompositeAction",
            value: actions,
            componentId: imageComponentId,
          });

          successToast(
            "Image uploaded successfully",
            "Uploaded image has been added to Replo.",
          );
        }
      }
    }

    if (imageComponentId) {
      // NOTE (Fran 2024-05-07): We want to always remove the id from the list of uploading components,
      // even if the component does not exist anymore.
      dispatch(resetEditorMediaUploadingComponentId(imageComponentId));
    }
  };

  const onError = (imageComponentId: string | null) => {
    if (imageComponentId) {
      dispatch(resetEditorMediaUploadingComponentId(imageComponentId));
    }
  };

  const { getRootProps, getInputProps } = useReploFileDropZone({
    onDropBeforeUpload,
    onUploadComplete,
    onError,
    acceptDropAssetType: IMAGE_ACCEPT_FILE_EXTENSIONS,
    allowClickToUpload: false,
  });

  return (
    <div
      {...getRootProps({
        className: twMerge("h-full w-full cursor-default", className),
        role,
        style,
      })}
    >
      <input {...getInputProps()} />
      {children}
    </div>
  );
};
